import { endOfDay, startOfDay } from "date-fns";
import { getSessionUserId, readStoredSession } from "./sessionStore";
import { businessTodayDate } from "./businessClock";
import { computePresetDateRange, normalizeDateRangeOption, type DateRangeOptionId } from "./dateRangePresets";

/**
 * Page filters kept for the browser session (sessionStorage), per user, so
 * leaving a page and coming back (or a refresh after a post) keeps the
 * filters the user chose instead of falling back to defaults.
 */
const PREFIX = "filters:";

const scopedKey = (key: string): string | null => {
	const userId = getSessionUserId(readStoredSession());
	return userId && Number.isFinite(userId) ? `${PREFIX}${key}:${userId}` : null;
};

export const readSessionFilter = <T>(key: string | null | undefined): T | undefined => {
	const storageKey = key ? scopedKey(key) : null;
	if (!storageKey) return undefined;
	try {
		const raw = sessionStorage.getItem(storageKey);
		return raw == null ? undefined : (JSON.parse(raw) as T);
	} catch {
		return undefined;
	}
};

export const writeSessionFilter = (key: string | null | undefined, value: unknown) => {
	const storageKey = key ? scopedKey(key) : null;
	if (!storageKey) return;
	try {
		if (value === undefined) sessionStorage.removeItem(storageKey);
		else sessionStorage.setItem(storageKey, JSON.stringify(value));
	} catch {
		// Storage full or blocked: the filter just isn't kept.
	}
};

/** Date range choice: a preset stays relative ("this month" is recomputed); custom keeps its days. */
export type StoredDateRange = { preset: DateRangeOptionId; from?: string | null; to?: string | null };

const dateRangeKey = (key: string) => `daterange:${key}`;

export const readStoredDateRange = (key: string | null | undefined): StoredDateRange | undefined => {
	if (!key) return undefined;
	const stored = readSessionFilter<StoredDateRange>(dateRangeKey(key));
	const preset = normalizeDateRangeOption(stored?.preset);
	return stored && preset ? { ...stored, preset } : undefined;
};

export const writeStoredDateRange = (key: string | null | undefined, value: StoredDateRange) => {
	if (key) writeSessionFilter(dateRangeKey(key), value);
};

const toDate = (value?: string | null): Date | null => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

/** The range a stored choice stands for today, or undefined when nothing was stored. */
export const resolveStoredDateRange = (key: string | null | undefined): { from: Date | null; to: Date | null } | undefined => {
	const stored = readStoredDateRange(key);
	if (!stored) return undefined;
	if (stored.preset === "custom") {
		const from = toDate(stored.from);
		const to = toDate(stored.to);
		return { from: from ? startOfDay(from) : null, to: to ? endOfDay(to) : null };
	}
	return computePresetDateRange(stored.preset, businessTodayDate()) ?? { from: null, to: null };
};
